'use client';

import { token } from '@terra/ui-styled-system';
import type { z } from 'zod';
import { FindingStatusSchema } from '@terra/shared-schemas-orchestrator';
import type { TerraIcon } from '@terra/ui-icons';
import {
  CheckmarkCircle02Icon,
  DashedLineCircleIcon,
  Clock01Icon,
  RefreshIcon,
  StatusIcon,
} from '@terra/ui-icons';
import { Badge, type BadgeProps } from '../badge';

export type BadgeStatusType =
  | 'New'
  | 'Unresolved'
  | 'Progress'
  | 'Retest'
  | 'Resolved';
export const BADGE_STATUS = {
  NEW: 'New',
  UNRESOLVED: 'Unresolved',
  PROGRESS: 'Progress',
  RETEST: 'Retest',
  RESOLVED: 'Resolved',
} as const satisfies Record<string, BadgeStatusType>;
export type BadgeStatusProps = Omit<BadgeProps, 'icon'> & {
  status: BadgeStatusType;
};
type BadgeStatusConfig = {
  icon: TerraIcon;
  iconColor: string;
};

export const BADGE_STATUS_CONFIG: Record<BadgeStatusType, BadgeStatusConfig> = {
  [BADGE_STATUS.NEW]: {
    icon: DashedLineCircleIcon,
    iconColor: token('colors.fg.status.new'),
  },
  [BADGE_STATUS.UNRESOLVED]: {
    icon: StatusIcon,
    iconColor: token('colors.fg.status.unresolved'),
  },
  [BADGE_STATUS.PROGRESS]: {
    icon: Clock01Icon,
    iconColor: token('colors.fg.status.inProgress'),
  },
  [BADGE_STATUS.RETEST]: {
    icon: RefreshIcon,
    iconColor: token('colors.fg.status.inProgress'),
  },
  [BADGE_STATUS.RESOLVED]: {
    icon: CheckmarkCircle02Icon,
    iconColor: token('colors.fg.status.resolved'),
  },
};
type FindingStatusValue = z.infer<typeof FindingStatusSchema>;

export const FINDING_STATUS_BADGE_STATUS_MAP: Record<
  FindingStatusValue,
  BadgeStatusType
> = {
  [FindingStatusSchema.enum.Resolved]: BADGE_STATUS.RESOLVED,
  [FindingStatusSchema.enum.Unresolved]: BADGE_STATUS.UNRESOLVED,
  [FindingStatusSchema.enum.InProgress]: BADGE_STATUS.PROGRESS,
  [FindingStatusSchema.enum.FalsePositive]: BADGE_STATUS.RESOLVED,
  [FindingStatusSchema.enum.WontFix]: BADGE_STATUS.RESOLVED,
  [FindingStatusSchema.enum.Retest]: BADGE_STATUS.RETEST,
};
const MAPPED_FINDING_STATUS_VALUES = Object.keys(
  FINDING_STATUS_BADGE_STATUS_MAP,
) as FindingStatusValue[];

export const isMappedFindingStatus = (
  value: unknown,
): value is FindingStatusValue =>
  typeof value === 'string' &&
  (MAPPED_FINDING_STATUS_VALUES as readonly string[]).includes(value);
export const BadgeStatus = ({
  status,
  variant = 'outline',
  size = 'md',
  children,
  ...props
}: BadgeStatusProps) => {
  const config = BADGE_STATUS_CONFIG[status];
  const StatusIcon = config.icon;

  return (
    <Badge
      variant={variant}
      size={size}
      icon={<StatusIcon size={16} color={config.iconColor} />}
      {...props}
    >
      {children}
    </Badge>
  );
};
